"use client";

import React from "react";
import { useFormatter, useTranslations } from "next-intl";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/i18n/routing";
import { insuranceTypeFamilyOrder } from "@/lib/data/insurance";
import type { InsuranceProviderCardItem } from "@/lib/fetch/insurance";
import InsuranceFamilyStrip, {
  getCoveredInsuranceFamilies,
  getInsuranceFamilyCounts,
} from "./InsuranceFamilyStrip";

type Props = {
  item: InsuranceProviderCardItem;
};

/** Search-result tile for a provider, matching the server-rendered card. */
const InsuranceProviderItem: React.FC<Props> = ({ item }) => {
  const t = useTranslations("insurance");
  const formatter = useFormatter();
  const url = `/resource/insurance/${item.slug}`;
  const linkTitle = t("card.link-title", { name: item.name });
  const familyCounts = getInsuranceFamilyCounts(item.types);
  const covered = getCoveredInsuranceFamilies(familyCounts);
  const familyLabels = Object.fromEntries(
    insuranceTypeFamilyOrder.map((family) => [family, t(`family.${family}`)]),
  ) as Record<(typeof insuranceTypeFamilyOrder)[number], string>;

  return (
    <article className="group flex h-full min-w-0 flex-col gap-4 rounded-2xl border border-border bg-background p-5 shadow-sm transition-all hover:border-zinc-300 hover:shadow-md">
      <div className="flex min-w-0 flex-col gap-0.5">
        <h3 className="min-w-0 text-lg font-semibold tracking-tight">
          <Link
            href={url}
            title={linkTitle}
            className="block truncate transition-colors hover:text-blue-700"
          >
            {item.name}
          </Link>
        </h3>
        <p className="truncate text-xs text-muted-foreground">
          {item.legalName ?? t("card.provider")}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-2">
        <div className="flex flex-col rounded-xl bg-slate-50 px-3 py-2.5">
          <dd className="order-1 text-2xl font-semibold leading-none tabular-nums">
            {formatter.number(item.productCount)}
          </dd>
          <dt className="order-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("card.stat-products")}
          </dt>
        </div>
        <div className="flex flex-col rounded-xl bg-slate-50 px-3 py-2.5">
          <dd className="order-1 text-2xl font-semibold leading-none tabular-nums">
            {formatter.number(item.typeCount)}
          </dd>
          <dt className="order-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("card.stat-types")}
          </dt>
        </div>
      </dl>

      <div className="flex flex-col gap-2">
        <InsuranceFamilyStrip counts={familyCounts} labels={familyLabels} />
        <p className="line-clamp-1 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">
            {t("card.families", { count: covered.length })}
          </span>
          {covered.length > 0 && (
            <>
              {" · "}
              {covered
                .slice(0, 3)
                .map((family) => familyLabels[family])
                .join(", ")}
            </>
          )}
        </p>
      </div>

      {item.matchedProducts && item.matchedProducts.length > 0 && (
        <p className="text-xs leading-5 text-muted-foreground">
          <span className="font-semibold text-foreground">
            {t("card.matches")}
          </span>{" "}
          {item.matchedProducts.join(", ")}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <span className="text-xs font-medium text-muted-foreground">
          {t("common.checked", {
            date: formatter.dateTime(new Date(item.lastVerifiedAt), {
              year: "numeric",
              month: "short",
            }),
          })}
        </span>
        <Link
          href={url}
          title={linkTitle}
          className="flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline"
        >
          {t("card.view")}
          <ArrowUpRight className="size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </article>
  );
};

export default InsuranceProviderItem;
